/**
 * Extension host store (editor-core).
 *
 * Small Zustand store backing the app-side glue for extensions:
 *  - per-extension workspace settings (persisted to localStorage), used by
 *    `HostSettingsServices` and the Settings Center panel;
 *  - the currently focused extension sidebar panel, so `host.focusPanel(id)`
 *    can drive the Sidebar reactively.
 *  - the currently visible extension sidebar panel, so host apps with custom
 *    activity buttons can avoid highlighting a built-in sidebar view at the
 *    same time.
 *
 * This deliberately stays independent of the heavier config/settings system:
 * extension settings are simple JSON blobs keyed by extension id.
 */
import { create } from "zustand";
import type { ReactNode } from "react";

const SETTINGS_STORAGE_KEY = "magic-md:extension-settings";
const ENABLED_STORAGE_KEY = "magic-md:extension-enabled";

export interface ExtensionSidebarPanelRef {
    extensionId: string;
    panelId: string;
}

export interface FloatingExtensionSidebarPanel extends ExtensionSidebarPanelRef {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ExtensionFloatingPanelInstance {
    instanceId: string;
    extensionId: string;
    panelId: string;
    title?: string;
    headerActions?: ReactNode;
    payload?: unknown;
    x: number;
    y: number;
    width: number;
    height: number;
}

function samePanel(
    left: ExtensionSidebarPanelRef | null,
    right: ExtensionSidebarPanelRef | null,
): boolean {
    if (!left || !right) return left === right;
    return left.extensionId === right.extensionId && left.panelId === right.panelId;
}

function loadPersisted(): Record<string, Record<string, unknown>> {
    if (typeof localStorage === "undefined") return {};
    try {
        const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Record<string, Record<string, unknown>>) : {};
    } catch {
        return {};
    }
}

function loadPersistedEnabled(): Record<string, boolean> {
    if (typeof localStorage === "undefined") return {};
    try {
        const raw = localStorage.getItem(ENABLED_STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
        return {};
    }
}

function persistSettings(value: Record<string, Record<string, unknown>>): void {
    if (typeof localStorage === "undefined") return;
    try {
        localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(value));
    } catch {
        /* ignore quota / serialization errors */
    }
}

function persistEnabled(value: Record<string, boolean>): void {
    if (typeof localStorage === "undefined") return;
    try {
        localStorage.setItem(ENABLED_STORAGE_KEY, JSON.stringify(value));
    } catch {
        /* ignore quota / serialization errors */
    }
}

interface ExtensionHostState {
    /** Per-extension workspace settings. */
    settings: Record<string, Record<string, unknown>>;
    /** Manifest-derived defaults, merged under workspace overrides. */
    defaults: Record<string, Record<string, unknown>>;
    /** Per-extension enable overrides. Missing means enabled by default. */
    enabled: Record<string, boolean>;
    /** Effective enablement after user/workspace/remoteHost layers are resolved. */
    effectiveEnabled: Record<string, boolean>;
    /** Currently focused extension sidebar panel id (null = none). */
    focusedPanelId: string | null;
    /** Currently visible extension sidebar panel (null = built-in sidebar view). */
    activeSidebarPanel: ExtensionSidebarPanelRef | null;
    /** Extension sidebar panel detached into a movable floating tool window. */
    floatingSidebarPanel: FloatingExtensionSidebarPanel | null;
    /** Arbitrary extension-owned floating UI instances. */
    floatingPanels: ExtensionFloatingPanelInstance[];

    registerDefaults(extensionId: string, defaults: Record<string, unknown>): void;
    getSettings(extensionId: string): Record<string, unknown>;
    setSettings(extensionId: string, next: Record<string, unknown>): void;
    clearSettings(extensionId: string): void;
    isEnabled(extensionId: string): boolean;
    setEnabled(extensionId: string, enabled: boolean): void;
    setEffectiveEnabled(next: Record<string, boolean>): void;
    focusPanel(panelId: string | null): void;
    setActiveSidebarPanel(panel: ExtensionSidebarPanelRef | null): void;
    floatSidebarPanel(panel: FloatingExtensionSidebarPanel): void;
    dockFloatingSidebarPanel(): ExtensionSidebarPanelRef | null;
    closeFloatingSidebarPanel(): void;
    moveFloatingSidebarPanel(x: number, y: number): void;
    resizeFloatingSidebarPanel(width: number, height: number): void;
    openFloatingPanel(panel: Omit<ExtensionFloatingPanelInstance, "instanceId"> & {
        instanceId?: string;
    }): string;
    closeFloatingPanel(instanceId: string): void;
    moveFloatingPanel(instanceId: string, x: number, y: number): void;
    resizeFloatingPanel(instanceId: string, width: number, height: number): void;
    setFloatingPanelHeaderActions(instanceId: string, actions: ReactNode | null): void;
}

export const useExtensionHostStore = create<ExtensionHostState>((set, get) => ({
    settings: loadPersisted(),
    defaults: {},
    enabled: loadPersistedEnabled(),
    effectiveEnabled: {},
    focusedPanelId: null,
    activeSidebarPanel: null,
    floatingSidebarPanel: null,
    floatingPanels: [],

    registerDefaults(extensionId, defaults) {
        set((state) => ({
            defaults: { ...state.defaults, [extensionId]: { ...defaults } },
        }));
    },

    getSettings(extensionId) {
        const { settings, defaults } = get();
        return { ...(defaults[extensionId] ?? {}), ...(settings[extensionId] ?? {}) };
    },

    setSettings(extensionId, next) {
        set((state) => {
            const settings = { ...state.settings, [extensionId]: { ...next } };
            persistSettings(settings);
            return { settings };
        });
    },

    clearSettings(extensionId) {
        set((state) => {
            if (!Object.hasOwn(state.settings, extensionId)) return {};
            const settings = { ...state.settings };
            delete settings[extensionId];
            persistSettings(settings);
            return { settings };
        });
    },

    isEnabled(extensionId) {
        const { effectiveEnabled, enabled } = get();
        return Object.hasOwn(effectiveEnabled, extensionId)
            ? effectiveEnabled[extensionId] !== false
            : enabled[extensionId] !== false;
    },

    setEnabled(extensionId, enabled) {
        set((state) => {
            const next = { ...state.enabled };
            if (enabled) delete next[extensionId];
            else next[extensionId] = false;
            persistEnabled(next);
            return {
                enabled: next,
                effectiveEnabled: {
                    ...state.effectiveEnabled,
                    [extensionId]: enabled,
                },
            };
        });
    },

    setEffectiveEnabled(next) {
        set({ effectiveEnabled: { ...next } });
    },

    focusPanel(panelId) {
        set({ focusedPanelId: panelId });
    },

    setActiveSidebarPanel(panel) {
        set((state) =>
            samePanel(state.activeSidebarPanel, panel)
                ? {}
                : { activeSidebarPanel: panel },
        );
    },

    floatSidebarPanel(panel) {
        const activePanel = {
            extensionId: panel.extensionId,
            panelId: panel.panelId,
        };
        set({
            floatingSidebarPanel: panel,
            activeSidebarPanel: activePanel,
            focusedPanelId: null,
        });
    },

    dockFloatingSidebarPanel() {
        const floating = get().floatingSidebarPanel;
        if (!floating) return null;
        const panel = {
            extensionId: floating.extensionId,
            panelId: floating.panelId,
        };
        set({ floatingSidebarPanel: null, activeSidebarPanel: panel });
        return panel;
    },

    closeFloatingSidebarPanel() {
        set({ floatingSidebarPanel: null, activeSidebarPanel: null });
    },

    moveFloatingSidebarPanel(x, y) {
        set((state) =>
            state.floatingSidebarPanel
                ? { floatingSidebarPanel: { ...state.floatingSidebarPanel, x, y } }
                : {},
        );
    },

    resizeFloatingSidebarPanel(width, height) {
        set((state) =>
            state.floatingSidebarPanel
                ? {
                      floatingSidebarPanel: {
                          ...state.floatingSidebarPanel,
                          width,
                          height,
                      },
                  }
                : {},
        );
    },

    openFloatingPanel(panel) {
        const instanceId =
            panel.instanceId ??
            `${panel.extensionId}:${panel.panelId}:${Date.now().toString(36)}:${Math.random()
                .toString(36)
                .slice(2, 7)}`;
        const nextPanel: ExtensionFloatingPanelInstance = { ...panel, instanceId };
        set((state) => ({
            floatingPanels: [
                ...state.floatingPanels.filter((item) => item.instanceId !== instanceId),
                nextPanel,
            ],
        }));
        return instanceId;
    },

    closeFloatingPanel(instanceId) {
        set((state) => ({
            floatingPanels: state.floatingPanels.filter(
                (panel) => panel.instanceId !== instanceId,
            ),
        }));
    },

    moveFloatingPanel(instanceId, x, y) {
        set((state) => ({
            floatingPanels: state.floatingPanels.map((panel) =>
                panel.instanceId === instanceId ? { ...panel, x, y } : panel,
            ),
        }));
    },

    resizeFloatingPanel(instanceId, width, height) {
        set((state) => ({
            floatingPanels: state.floatingPanels.map((panel) =>
                panel.instanceId === instanceId ? { ...panel, width, height } : panel,
            ),
        }));
    },

    setFloatingPanelHeaderActions(instanceId, actions) {
        set((state) => ({
            floatingPanels: state.floatingPanels.map((panel) =>
                panel.instanceId === instanceId
                    ? { ...panel, headerActions: actions ?? undefined }
                    : panel,
            ),
        }));
    },
}));

/** Snapshot helper for non-React callers (host services). */
export function getExtensionSettings(extensionId: string): Record<string, unknown> {
    return useExtensionHostStore.getState().getSettings(extensionId);
}
